import React, { useEffect, useState, useRef } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { auth, db, updateProfile } from '../../components/firebase';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUser, FiLogOut, FiEdit3, FiSave, FiX, FiExternalLink,
  FiBook, FiShield, FiCamera, FiCalendar, FiSettings,
  FiArrowRight, FiUploadCloud, FiClock, FiPlus, FiTrash2,
  FiCheckCircle, FiInfo, FiBriefcase, FiGithub, FiLinkedin, FiGlobe
} from 'react-icons/fi';
import { FaGraduationCap, FaCoins, FaUniversity } from 'react-icons/fa';
import Footer from '../../components/Footer';
import BackButton from '../../components/BackButton';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';

const Profile = () => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [branch, setBranch] = useState('');
  const [semester, setSemester] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [college, setCollege] = useState('');
  const [website, setWebsite] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [twitter, setTwitter] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [locationStr, setLocationStr] = useState('');
  const [userContributions, setUserContributions] = useState([]);
  // LinkedIn-style student fields
  const [headline, setHeadline] = useState('');
  const [githubUrl, setGithubUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [portfolioUrl, setPortfolioUrl] = useState('');
  const [skills, setSkills] = useState('');
  const [availableForWork, setAvailableForWork] = useState(false);
  const [experiences, setExperiences] = useState([]);
  const [projects, setProjects] = useState([]);
  const [uploadStep, setUploadStep] = useState(1); // 1: Select, 2: Meta, 3: Success
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMeta, setUploadMeta] = useState({ unit: '', subject: '', semester: '', branch: '' });
  const [contributionSaving, setContributionSaving] = useState(false);
  const fileRef = useRef();
  const pdfRef = useRef();
  const navigate = useNavigate();
  useTheme();
  const { isAdmin, isCompany, user, userProfile: authProfile } = useAuth();
  const location = useLocation();

  const c = {
    bg: 'var(--bg-main)',
    card: 'var(--bg-card)',
    border: 'var(--border)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    surface: 'var(--bg-surface)',
    accent: 'var(--accent)',
    glow: 'var(--accent-rgb, 34, 197, 94)',
  };

  const inputStyle = {
    width: '100%', height: 42,
    background: c.surface, border: `1px solid ${c.border}`,
    borderRadius: 9, padding: '0 14px',
    color: c.text, fontSize: 14, outline: 'none',
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'contribution') {
      setActiveTab('contribution');
    } else if (tab === 'profile') {
      setActiveTab('profile');
    }
  }, [location]);

  // Sync local state with authProfile when it's available
  useEffect(() => {
    if (authProfile) {
      setUserDetails(authProfile);
      setDisplayName(authProfile.fullName || authProfile.displayName || '');
      setBio(authProfile.bio || '');
      setBranch(authProfile.branch || '');
      setSemester(authProfile.semester || '');
      setCollege(authProfile.college || authProfile.companyName || '');
      setWebsite(authProfile.website || '');
      setLinkedin(authProfile.linkedin || '');
      setTwitter(authProfile.twitter || '');
      setCompanySize(authProfile.companySize || '');
      setLocationStr(authProfile.location || '');
      // LinkedIn-style fields
      setHeadline(authProfile.headline || '');
      setGithubUrl(authProfile.githubUrl || '');
      setLinkedinUrl(authProfile.linkedinUrl || '');
      setPortfolioUrl(authProfile.portfolioUrl || '');
      setSkills(Array.isArray(authProfile.skills) ? authProfile.skills.join(', ') : (authProfile.skills || ''));
      setAvailableForWork(authProfile.availableForWork || false);
      setExperiences(authProfile.experiences || []);
      setProjects(authProfile.projects || []);
      setLoading(false);
    }
  }, [authProfile]);

  useEffect(() => {
    if (!user && !loading) return; // Wait for initial auth

    // Fetch User Contributions
    const q = query(collection(db, 'contributions'), where('userId', '==', user?.uid || ''));
    const unsubContribs = onSnapshot(q, (snapshot) => {
      const cData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUserContributions(cData.sort((a, b) => b.timestamp?.toMillis() - a.timestamp?.toMillis()));
    });

    return () => unsubContribs();
  }, [user, loading]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const u = auth.currentUser;
      const updates = {
        fullName: displayName, // Map displayName to fullName for consistency
        displayName,
        bio,
        college
      };

      if (!isCompany) {
        updates.branch = branch;
        updates.semester = semester;
        // LinkedIn-style fields for students
        updates.headline = headline;
        updates.githubUrl = githubUrl;
        updates.linkedinUrl = linkedinUrl;
        updates.portfolioUrl = portfolioUrl;
        updates.skills = skills.split(',').map(s => s.trim()).filter(Boolean);
        updates.availableForWork = availableForWork;
        updates.experiences = experiences;
        updates.projects = projects;
      } else {
        updates.website = website;
        updates.linkedin = linkedin;
        updates.twitter = twitter;
        updates.companySize = companySize;
        updates.location = locationStr;
        updates.companyName = college; // Sync companyName
      }

      // Update Users Collection
      await updateDoc(doc(db, 'Users', u.uid), updates);

      // If Company, also update Companies Collection
      if (isCompany) {
        await updateDoc(doc(db, 'Companies', u.uid), {
          representativeName: displayName,
          companyName: college,
          website,
          linkedin,
          twitter,
          companySize,
          location: locationStr,
          updatedAt: serverTimestamp()
        });
      }

      setUserDetails(prev => ({ ...prev, ...updates }));
      toast.success('Profile updated!');
      setEditing(false);
      setIsEditModalOpen(false);
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const handleContributeUpload = async () => {
    if (!selectedFile || !uploadMeta.subject || !uploadMeta.unit || !uploadMeta.branch || !uploadMeta.semester) {
      toast.error('Please fill all details');
      return;
    }

    const scriptUrl = process.env.REACT_APP_GOOGLE_DRIVE_SCRIPT_URL;
    if (!scriptUrl) {
      toast.error('Upload system not configured. Please add Script URL to .env');
      return;
    }

    setContributionSaving(true);
    try {
      const u = auth.currentUser;
      const fileName = `${uploadMeta.unit}_${uploadMeta.subject}_${uploadMeta.semester}_${uploadMeta.branch}_${u.email}.pdf`
        .replace(/\s+/g, '_').toLowerCase();

      // Convert file to Base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(selectedFile);
      });

      const base64Data = await base64Promise;

      // Upload to Google Drive via Apps Script
      // mode: 'no-cors' is the only bulletproof way to send data to Apps Script from localhost.
      // We won't be able to read the response, but the data WILL be sent.
      await fetch(scriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ fileName, base64Data }),
      });

      // Save to Firestore (Predictable filename for Admin)
      await addDoc(collection(db, 'contributions'), {
        userId: u.uid,
        userEmail: u.email,
        fileName,
        fileUrl: 'View in Drive', // Admin can search Drive by filename
        metadata: uploadMeta,
        status: 'Pending',
        timestamp: serverTimestamp()
      });

      toast.success('Contribution submitted! Check your Drive folder.');
      setUploadStep(3);
      setSelectedFile(null);
      setUploadMeta({ unit: '', subject: '', semester: '', branch: '' });
    } catch (e) {
      console.error(e);
      toast.error("Upload failed: " + e.message);
    }
    setContributionSaving(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file.'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result;
        const u = auth.currentUser;
        // Update Firestore — AuthContext's onSnapshot will pick this up automatically
        await updateDoc(doc(db, 'Users', u.uid), { photo: base64 });
        // Also update Firebase Auth photoURL for fallback
        await updateProfile(u, { photoURL: base64 });
        // Update local state immediately for instant UI feedback
        setUserDetails(prev => ({ ...prev, photo: base64 }));
        toast.success('Photo updated!');
      } catch (e) {
        toast.error(e.message);
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  // Always read photo from authProfile so it reflects Firestore real-time updates
  const avatar = authProfile?.photo || userDetails?.photo || user?.photoURL;
  const name = authProfile?.displayName || authProfile?.fullName || userDetails?.displayName || user?.displayName || (isCompany ? 'Company Representative' : 'Student');
  const roleLabel = isCompany ? 'Company' : (isAdmin ? 'Administrator' : 'Student');
  const email = authProfile?.email || userDetails?.email || user?.email || '';

  const stats = isCompany
    ? [
      { icon: <FiBriefcase />, label: 'Jobs Posted', value: userDetails?.jobsPosted || '0' },
      { icon: <FaUniversity />, label: 'Company', value: userDetails?.college || 'Not set' },
    ]
    : [
      { icon: <FaCoins />, label: 'Points', value: userDetails?.points || '0' },
      { icon: <FaUniversity />, label: 'College', value: userDetails?.college || 'Not set' },
      { icon: <FaGraduationCap />, label: 'Branch', value: userDetails?.branch?.toUpperCase() || '—' },
      { icon: <FiCalendar />, label: 'Semester', value: userDetails?.semester ? `${userDetails.semester} Sem` : '—' },
    ];

  if (loading) {
    return (
      <div style={{ background: c.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
          style={{ width: 36, height: 36, borderRadius: '50%', border: `3px solid ${c.accent}`, borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div style={{ background: c.bg, minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      {/* Back Button */}
      <div style={{ position: 'absolute', top: '24px', left: '24px', zIndex: 100 }}>
        <BackButton to="/home" label="Back to Home" />
      </div>
      <ToastContainer position="top-right" theme="dark" />

      {/* Background Glows */}
      <div style={{
        position: 'absolute', top: '-10%', right: '-5%',
        width: '500px', height: '500px',
        background: `radial-gradient(circle, rgba(34,197,94,0.1) 0%, transparent 70%)`,
        filter: 'blur(80px)', pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', bottom: '15%', left: '-5%',
        width: '400px', height: '400px',
        background: `radial-gradient(circle, rgba(34,197,94,0.08) 0%, transparent 70%)`,
        filter: 'blur(60px)', pointerEvents: 'none', zIndex: 0
      }} />

      {/* ── PROFILE HEADER ──────────────────────────────────────────── */}
      <section style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%)',
        borderBottom: `1px solid ${c.border}`,
        padding: 'clamp(60px,8vw,100px) 20px 0',
        position: 'relative', zIndex: 1
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Avatar row */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, marginBottom: 24, flexWrap: 'wrap' }}>
            {/* Avatar */}
            <div style={{ position: 'relative' }}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                style={{
                  width: 100, height: 100, borderRadius: '24px',
                  background: `linear-gradient(135deg, ${c.accent}, #16a34a)`,
                  overflow: 'hidden',
                  border: `2px solid rgba(255,255,255,0.1)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: `0 10px 25px -5px ${c.glow}`,
                  position: 'relative'
                }}
                onClick={() => fileRef.current?.click()}
              >
                {avatar
                  ? <img src={avatar} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <div style={{
                    width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,255,255,0.1)',
                    fontSize: '40px', fontWeight: 800, color: '#fff'
                  }}>
                    {name.charAt(0).toUpperCase()}
                  </div>}
              </motion.div>

              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => fileRef.current?.click()}
                style={{
                  position: 'absolute', bottom: -5, right: -5,
                  width: 32, height: 32, borderRadius: '10px',
                  background: 'var(--accent)', border: `2px solid var(--bg-main)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#fff',
                  boxShadow: 'var(--card-shadow)'
                }}
              >
                {uploading
                  ? <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.6, ease: 'linear' }} style={{ width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%' }} />
                  : <FiCamera size={14} />}
              </motion.button>
              <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
            </div>

            {/* Name + role */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: 'clamp(1.3rem,3vw,1.8rem)', fontWeight: 800, color: c.text, letterSpacing: '-0.02em' }}>
                  {name}
                </h1>
                <span style={{
                  padding: '4px 12px', borderRadius: 8,
                  background: isCompany ? 'rgba(34,197,94,0.1)' : 'rgba(163,230,53,0.1)',
                  border: `1px solid ${isCompany ? 'rgba(34,197,94,0.2)' : 'rgba(163,230,53,0.2)'}`,
                  fontSize: 12, fontWeight: 700, color: isCompany ? c.accent : '#a3e635',
                  textTransform: 'uppercase', letterSpacing: '0.05em'
                }}>
                  {roleLabel}
                </span>
              </div>
              <div style={{ fontSize: 14, color: c.muted, marginTop: 4 }}>{email}</div>
              {userDetails?.bio && (
                <div style={{ fontSize: 13, color: c.muted, marginTop: 6, maxWidth: 400 }}>{userDetails.bio}</div>
              )}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <motion.button
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={() => setIsEditModalOpen(true)}
                style={{
                  height: 40, padding: '0 20px',
                  background: c.accent,
                  border: 'none',
                  borderRadius: 12, cursor: 'pointer',
                  color: '#fff',
                  fontSize: 14, fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: 8,
                  boxShadow: `0 4px 15px ${c.glow}`
                }}
              >
                <><FiEdit3 size={16} /> Edit Profile</>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={handleLogout}
                style={{
                  height: 40, padding: '0 20px',
                  background: 'rgba(239,68,68,0.05)',
                  border: '1px solid rgba(239,68,68,0.15)',
                  borderRadius: 12, cursor: 'pointer',
                  color: '#ef4444',
                  fontSize: 14, fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <FiLogOut size={16} /> Logout
              </motion.button>
            </div>
          </div>

          {/* Stats strip */}
          <div style={{
            display: 'flex', gap: 0,
            borderTop: `1px solid ${c.border}`,
          }}>
            {stats.map((s, i) => (
              <div
                key={s.label}
                style={{
                  flex: 1, padding: '16px 12px', textAlign: 'center',
                  borderRight: i < stats.length - 1 ? `1px solid ${c.border}` : 'none',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 800, color: c.text, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <span style={{ color: c.accent, opacity: 0.8 }}>{s.icon}</span>
                  {s.value}
                </div>
                <div style={{ fontSize: 10, color: c.muted, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* TABS */}
          <div style={{ display: 'flex', gap: 32, marginTop: 40, borderBottom: `1px solid ${c.border}` }}>
            {['profile', isCompany ? 'jobs posted' : 'contribution'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab === 'jobs posted' ? 'contribution' : tab)}
                style={{
                  background: 'none', border: 'none', padding: '12px 4px', cursor: 'pointer',
                  color: (activeTab === tab || (activeTab === 'contribution' && tab === 'jobs posted')) ? c.accent : c.muted,
                  fontSize: 14, fontWeight: 700, textTransform: 'capitalize',
                  position: 'relative', display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                {tab}
                {(activeTab === tab || (activeTab === 'contribution' && tab === 'jobs posted')) && (
                  <motion.div layoutId="activeTab" style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 2, background: c.accent }} />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ────────────────────────────────────────────── */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px 80px', position: 'relative', zIndex: 1 }}>
        <AnimatePresence mode="wait">
          {activeTab === 'profile' ? (
            <motion.div
              key="profile"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(350px,1fr))', gap: 24 }}
            >
              {/* Edit / Info Card */}
              <AnimatePresence mode="wait">
                <motion.div
                  key="info"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 16, padding: 24 }}
                >
                  <div style={{ fontSize: 15, fontWeight: 700, color: c.text, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiUser size={15} style={{ color: '#a3e635' }} /> Account Info
                  </div>
                  {[
                    { label: isCompany ? 'Representative' : 'Name', value: name },
                    { label: 'Email', value: email },
                    { label: isCompany ? 'Company Name' : 'College', value: userDetails?.college || 'Not set' },
                    { label: 'Role', value: roleLabel },
                    ...(!isCompany ? [
                      { label: 'Branch', value: userDetails?.branch?.toUpperCase() || 'Not set' },
                      { label: 'Semester', value: userDetails?.semester ? `${userDetails.semester} Semester` : 'Not set' },
                    ] : [
                      { label: 'Website', value: userDetails?.website || 'Not set' },
                      { label: 'Company Size', value: userDetails?.companySize || 'Not set' },
                      { label: 'Location', value: userDetails?.location || 'Not set' },
                    ]),
                  ].map(({ label, value }) => (
                    <div key={label} style={{
                      display: 'flex', justifyContent: 'space-between', padding: '10px 0',
                      borderBottom: `1px solid ${c.border}`, fontSize: 13,
                    }}>
                      <span style={{ color: c.muted }}>{label}</span>
                      <span style={{ color: c.text, fontWeight: 600 }}>{value}</span>
                    </div>
                  ))}
                </motion.div>
              </AnimatePresence>

              {/* Quick Links Card */}
              <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 16, padding: 24 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: c.text, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiSettings size={15} style={{ color: '#a3e635' }} /> Quick Actions
                </div>
                {[
                  ...(isCompany ? [
                    { label: 'Company Dashboard', icon: <FiBriefcase />, to: '/job-portal/company/dashboard' },
                  ] : [
                    { label: 'View Public Profile', icon: <FiExternalLink />, to: `/portfolio/${user?.uid}` },
                  ]),
                  { label: isCompany ? 'Post a Job' : 'Browse Notes', icon: isCompany ? <FiPlus /> : <FiBook />, to: isCompany ? '/job-portal/company/dashboard' : '/btech/cse/1st' },
                  { label: 'CodeCampus', icon: <FaGraduationCap />, to: '/tech-library' },
                  { label: 'Events Hub', icon: <FiCalendar />, to: '/event' },
                  ...(isAdmin ? [{ label: 'Admin Panel', icon: <FiShield />, to: '/admin' }] : []),
                ].map(({ label, icon, to }) => (
                  <Link key={to} to={to} style={{ textDecoration: 'none' }}>
                    <motion.div
                      whileHover={{ x: 4 }}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '11px 0',
                        borderBottom: `1px solid ${c.border}`,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 10,
                          background: 'rgba(34,197,94,0.1)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: c.accent, fontSize: 14,
                        }}>
                          {icon}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{label}</span>
                      </div>
                      <FiArrowRight size={14} style={{ color: c.muted }} />
                    </motion.div>
                  </Link>
                ))}

                {/* Info Box */}
                <div style={{ marginTop: 20, padding: 16, borderRadius: 12, background: isCompany ? 'rgba(34,197,94,0.05)' : 'rgba(163,230,53,0.05)', border: `1px solid ${isCompany ? 'rgba(34,197,94,0.1)' : 'rgba(163,230,53,0.1)'}` }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: isCompany ? c.accent : '#a3e635', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    {isCompany ? <FiCheckCircle /> : <FaCoins />} {isCompany ? 'Hire Top Talent' : 'Earn Rewards'}
                  </div>
                  <p style={{ fontSize: 11, color: c.muted, margin: 0, lineHeight: 1.5 }}>
                    {isCompany
                      ? 'Post job opportunities and find the best engineering talent from IPU University.'
                      : 'Contribute study materials and earn points to unlock premium features and recognition.'
                    }
                  </p>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="contribution"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(350px,1fr))', gap: 24 }}
            >
              {isCompany ? (
                /* COMPANY JOBS VIEW */
                <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 24, padding: 32, gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.accent }}>
                      <FiBriefcase size={24} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, color: c.text, fontWeight: 800 }}>Jobs Posted</h3>
                      <p style={{ margin: 0, fontSize: 12, color: c.muted }}>Manage your active listings</p>
                    </div>
                  </div>

                  <div style={{ textAlign: 'center', padding: '60px 0', border: `2px dashed ${c.border}`, borderRadius: 20 }}>
                    <FiBriefcase size={40} style={{ color: c.muted, marginBottom: 16, opacity: 0.5 }} />
                    <div style={{ fontSize: 16, fontWeight: 600, color: c.text }}>No jobs posted yet</div>
                    <p style={{ fontSize: 13, color: c.muted, marginTop: 8 }}>Go to your dashboard to create your first job listing.</p>
                    <button
                      onClick={() => navigate('/job-portal/company/dashboard')}
                      style={{
                        marginTop: 20, padding: '10px 24px', borderRadius: 12, background: c.accent, border: 'none',
                        color: '#000', fontSize: 14, fontWeight: 700, cursor: 'pointer'
                      }}
                    >Open Dashboard</button>
                  </div>
                </div>
              ) : (
                /* STUDENT CONTRIBUTION FORM */
                <>
                  <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 24, padding: 32, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.accent }}>
                        <FiUploadCloud size={24} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 18, color: c.text, fontWeight: 800 }}>Contribute</h3>
                        <p style={{ margin: 0, fontSize: 12, color: c.muted }}>Upload study materials</p>
                      </div>
                    </div>

                    {uploadStep === 1 && (
                      <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        onClick={() => pdfRef.current?.click()}
                        style={{
                          flex: 1, minHeight: 240, border: `2px dashed ${c.border}`, borderRadius: 20,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', transition: 'all 0.2s', gap: 16
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = c.accent}
                        onMouseLeave={e => e.currentTarget.style.borderColor = c.border}
                      >
                        <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(34,197,94,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.accent }}>
                          <FiUploadCloud size={30} />
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 15, fontWeight: 700, color: c.text }}>Drop PDF here</div>
                          <div style={{ fontSize: 12, color: c.muted, marginTop: 4 }}>or click to browse</div>
                        </div>
                        <button style={{
                          padding: '8px 24px', borderRadius: 10, background: c.surface, border: `1px solid ${c.border}`,
                          color: c.text, fontSize: 13, fontWeight: 700
                        }}>Select File</button>
                        <input ref={pdfRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={e => {
                          if (e.target.files[0]) { setSelectedFile(e.target.files[0]); setUploadStep(2); }
                        }} />
                      </motion.div>
                    )}

                    {uploadStep === 2 && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                        <div style={{ background: c.surface, padding: 16, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                          <FiBook size={20} color={c.accent} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedFile?.name}</div>
                            <div style={{ fontSize: 11, color: c.muted }}>{(selectedFile?.size / (1024 * 1024)).toFixed(2)} MB • PDF Document</div>
                          </div>
                          <FiX size={18} style={{ cursor: 'pointer' }} onClick={() => { setSelectedFile(null); setUploadStep(1); }} />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Subject</label>
                            <input style={inputStyle} value={uploadMeta.subject} onChange={e => setUploadMeta(p => ({ ...p, subject: e.target.value }))} placeholder="e.g. Physics" />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Unit</label>
                            <select style={inputStyle} value={uploadMeta.unit} onChange={e => setUploadMeta(p => ({ ...p, unit: e.target.value }))}>
                              <option value="">Select Unit</option>
                              {[1, 2, 3, 4, 5, 'Full Notes'].map(u => <option key={u} value={typeof u === 'number' ? `Unit-${u}` : u}>{u}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Branch</label>
                            <select style={inputStyle} value={uploadMeta.branch} onChange={e => setUploadMeta(p => ({ ...p, branch: e.target.value }))}>
                              <option value="">Select Branch</option>
                              <option value="cse">CSE</option>
                              <option value="aiml">AIML</option>
                              <option value="aids">AIDS</option>
                              <option value="it">IT</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 6, textTransform: 'uppercase' }}>Semester</label>
                            <select style={inputStyle} value={uploadMeta.semester} onChange={e => setUploadMeta(p => ({ ...p, semester: e.target.value }))}>
                              <option value="">Semester</option>
                              {['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                        </div>

                        <motion.button
                          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                          onClick={handleContributeUpload}
                          disabled={contributionSaving}
                          style={{
                            width: '100%', height: 48, marginTop: 24, background: c.accent, border: 'none', borderRadius: 14,
                            color: '#fff', fontSize: 15, fontWeight: 700, cursor: contributionSaving ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                            boxShadow: `0 8px 20px ${c.glow}40`
                          }}
                        >
                          {contributionSaving ? (
                            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} style={{ width: 18, height: 18, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%' }} />
                          ) : (
                            <><FiUploadCloud size={20} /> Submit Contribution</>
                          )}
                        </motion.button>
                      </motion.div>
                    )}

                    {uploadStep === 3 && (
                      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                          <FiCheckCircle size={32} />
                        </div>
                        <h4 style={{ margin: 0, color: c.text, fontSize: 18, fontWeight: 800 }}>Success!</h4>
                        <p style={{ color: c.muted, fontSize: 13, marginTop: 8 }}>Your quality content has been submitted. We'll review it shortly.</p>
                        <button
                          onClick={() => setUploadStep(1)}
                          style={{
                            marginTop: 20, padding: '10px 24px', borderRadius: 12, background: c.surface, border: `1px solid ${c.border}`,
                            color: c.accent, fontSize: 14, fontWeight: 700, cursor: 'pointer'
                          }}
                        >Upload Another</button>
                      </motion.div>
                    )}
                  </div>

                  {/* STATUS / UNDER REVIEW CARD */}
                  <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 24, padding: 32 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}>
                        <FiClock size={24} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 18, color: c.text, fontWeight: 800 }}>Under Review</h3>
                        <p style={{ margin: 0, fontSize: 12, color: c.muted }}>Pending approval</p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {userContributions.length > 0 ? (
                        userContributions.map((item, idx) => (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.05 }}
                            style={{
                              padding: 16, borderRadius: 16, background: c.surface, border: `1px solid ${c.border}`,
                              position: 'relative', overflow: 'hidden'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                              <span style={{
                                fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 6,
                                background: item.status === 'Approved' ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.1)',
                                color: item.status === 'Approved' ? '#22c55e' : '#f59e0b',
                                textTransform: 'uppercase'
                              }}>
                                {item.status}
                              </span>
                              <span style={{ fontSize: 10, color: c.muted }}>
                                {item.timestamp?.toDate().toLocaleDateString()}
                              </span>
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.metadata.subject} - {item.metadata.unit}
                            </div>
                            <div style={{ fontSize: 11, color: c.muted, marginTop: 4 }}>
                              {item.metadata.branch.toUpperCase()} • {item.metadata.semester} Sem
                            </div>
                          </motion.div>
                        ))
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px 0', opacity: 0.5 }}>
                          <FiInfo size={32} style={{ marginBottom: 12 }} />
                          <div style={{ fontSize: 13, fontWeight: 600 }}>No contributions yet</div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </section >

      <Footer />

      {/* Stepwise Edit Modal */}
      <StepwiseEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        isMobile={isMobile}
        isCompany={isCompany}
        c={c}
        inputStyle={inputStyle}
        formData={{
          displayName, displayNameSetter: setDisplayName,
          college, collegeSetter: setCollege,
          bio, bioSetter: setBio,
          branch, branchSetter: setBranch,
          semester, semesterSetter: setSemester,
          headline, headlineSetter: setHeadline,
          githubUrl, githubUrlSetter: setGithubUrl,
          linkedinUrl, linkedinUrlSetter: setLinkedinUrl,
          portfolioUrl, portfolioUrlSetter: setPortfolioUrl,
          skills, skillsSetter: setSkills,
          availableForWork, availableForWorkSetter: setAvailableForWork,
          experiences, experiencesSetter: setExperiences,
          projects, projectsSetter: setProjects,
          website, websiteSetter: setWebsite,
          linkedin, linkedinSetter: setLinkedin,
          twitter, twitterSetter: setTwitter,
          companySize, companySizeSetter: setCompanySize,
          locationStr, locationStrSetter: setLocationStr
        }}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  );
};

const StepwiseEditModal = ({ isOpen, onClose, isMobile, isCompany, c, inputStyle, formData, onSave, saving }) => {
  const [step, setStep] = useState(1);
  const totalSteps = isCompany ? 3 : 6;

  const nextStep = () => setStep(s => Math.min(s + 1, totalSteps));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  if (!isOpen) return null;

  const renderStep = () => {
    switch (step) {
      case 1: // Personal
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>{isCompany ? 'Representative Name' : 'Full Name'}</label>
              <input style={inputStyle} value={formData.displayName} onChange={e => formData.displayNameSetter(e.target.value)} placeholder="Enter name" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Bio / Description</label>
              <textarea style={{ ...inputStyle, height: 100, padding: '12px 14px', resize: 'none' }} value={formData.bio} onChange={e => formData.bioSetter(e.target.value)} placeholder="Write a short bio..." />
            </div>
            {isCompany && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Headquarters</label>
                <input style={inputStyle} value={formData.locationStr} onChange={e => formData.locationStrSetter(e.target.value)} placeholder="City, Country" />
              </div>
            )}
          </div>
        );
      case 2: // Academic (Student) / Company Info (Company)
        if (!isCompany) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>College / University</label>
                <input style={inputStyle} value={formData.college} onChange={e => formData.collegeSetter(e.target.value)} placeholder="Enter college name" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Branch</label>
                  <select style={inputStyle} value={formData.branch} onChange={e => formData.branchSetter(e.target.value)}>
                    <option value="">Select Branch</option>
                    <option value="cse">CSE</option>
                    <option value="aiml">AIML</option>
                    <option value="aids">AIDS</option>
                    <option value="it">IT</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Semester</label>
                  <select style={inputStyle} value={formData.semester} onChange={e => formData.semesterSetter(e.target.value)}>
                    <option value="">Select Semester</option>
                    {['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th'].map(s => <option key={s} value={s}>{s} Sem</option>)}
                  </select>
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Company Name</label>
                <input style={inputStyle} value={formData.college} onChange={e => formData.collegeSetter(e.target.value)} placeholder="Enter company name" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Company Size</label>
                <select style={inputStyle} value={formData.companySize} onChange={e => formData.companySizeSetter(e.target.value)}>
                  <option value="">Select Size</option>
                  <option value="1-10">1-10 Employees</option>
                  <option value="11-50">11-50 Employees</option>
                  <option value="51-200">51-200 Employees</option>
                  <option value="201+">201+ Employees</option>
                </select>
              </div>
            </div>
          );
        }
      case 3: // Professional (Student) / Links (Company)
        if (!isCompany) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Headline</label>
                <input style={inputStyle} value={formData.headline} onChange={e => formData.headlineSetter(e.target.value)} placeholder="e.g. Fullstack Developer | React Enthusiast" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Skills (Comma separated)</label>
                <input style={inputStyle} value={formData.skills} onChange={e => formData.skillsSetter(e.target.value)} placeholder="React, Node.js, Python..." />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '12px', background: formData.availableForWork ? `${c.accent}10` : c.surface, border: `1px solid ${formData.availableForWork ? c.accent : c.border}`, borderRadius: 12 }}>
                <input type="checkbox" checked={formData.availableForWork} onChange={e => formData.availableForWorkSetter(e.target.checked)} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>Available for Work / Internships</span>
              </label>
            </div>
          );
        } else {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Website</label>
                <input style={inputStyle} value={formData.website} onChange={e => formData.websiteSetter(e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>LinkedIn</label>
                <input style={inputStyle} value={formData.linkedin} onChange={e => formData.linkedinSetter(e.target.value)} placeholder="LinkedIn Profile" />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Twitter</label>
                <input style={inputStyle} value={formData.twitter} onChange={e => formData.twitterSetter(e.target.value)} placeholder="@handle" />
              </div>
            </div>
          );
        }
      case 4: // Experience (Student)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 800 }}>Professional Experience</span>
              <button onClick={() => formData.experiencesSetter([...formData.experiences, { title: '', company: '', startDate: '', endDate: '' }])} style={{ background: 'none', border: `1px solid ${c.accent}`, color: c.accent, padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Add</button>
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, paddingRight: 4 }}>
              {formData.experiences.map((exp, i) => (
                <div key={i} style={{ padding: 16, background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, position: 'relative' }}>
                  <button onClick={() => formData.experiencesSetter(formData.experiences.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><FiTrash2 size={14} /></button>
                  <input style={{ ...inputStyle, height: 36, marginBottom: 8 }} placeholder="Title" value={exp.title} onChange={e => { const a = [...formData.experiences]; a[i].title = e.target.value; formData.experiencesSetter(a); }} />
                  <input style={{ ...inputStyle, height: 36 }} placeholder="Company" value={exp.company} onChange={e => { const a = [...formData.experiences]; a[i].company = e.target.value; formData.experiencesSetter(a); }} />
                </div>
              ))}
              {formData.experiences.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: c.muted, fontSize: 13 }}>No experience added yet</div>}
            </div>
          </div>
        );
      case 5: // Projects (Student)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 800 }}>Key Projects</span>
              <button onClick={() => formData.projectsSetter([...formData.projects, { title: '', technologies: '', link: '' }])} style={{ background: 'none', border: `1px solid ${c.accent}`, color: c.accent, padding: '4px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Add</button>
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, paddingRight: 4 }}>
              {formData.projects.map((proj, i) => (
                <div key={i} style={{ padding: 16, background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, position: 'relative' }}>
                  <button onClick={() => formData.projectsSetter(formData.projects.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}><FiTrash2 size={14} /></button>
                  <input style={{ ...inputStyle, height: 36, marginBottom: 8 }} placeholder="Project Title" value={proj.title} onChange={e => { const a = [...formData.projects]; a[i].title = e.target.value; formData.projectsSetter(a); }} />
                  <input style={{ ...inputStyle, height: 36 }} placeholder="Tech Stack" value={proj.technologies} onChange={e => { const a = [...formData.projects]; a[i].technologies = e.target.value; formData.projectsSetter(a); }} />
                </div>
              ))}
              {formData.projects.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: c.muted, fontSize: 13 }}>No projects added yet</div>}
            </div>
          </div>
        );
      case 6: // Links (Student)
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>GitHub</label>
              <input style={inputStyle} value={formData.githubUrl} onChange={e => formData.githubUrlSetter(e.target.value)} placeholder="https://github.com/..." />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>LinkedIn</label>
              <input style={inputStyle} value={formData.linkedinUrl} onChange={e => formData.linkedinUrlSetter(e.target.value)} placeholder="https://linkedin.com/in/..." />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 8 }}>Portfolio</label>
              <input style={inputStyle} value={formData.portfolioUrl} onChange={e => formData.portfolioUrlSetter(e.target.value)} placeholder="https://..." />
            </div>
          </div>
        );
      default: return null;
    }
  };

  const modalContent = (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: c.bg,
      borderRadius: isMobile ? 0 : 24,
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Edit Profile</h3>
          <p style={{ margin: 0, fontSize: 11, color: c.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>Step {step} of {totalSteps}</p>
        </div>
        <button onClick={onClose} style={{ background: c.surface, border: `1px solid ${c.border}`, width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: c.text }}><FiX size={18} /></button>
      </div>

      {/* Progress Bar */}
      <div style={{ height: 3, background: c.border, width: '100%' }}>
        <motion.div animate={{ width: `${(step / totalSteps) * 100}%` }} style={{ height: '100%', background: c.accent }} />
      </div>

      {/* Body */}
      <div style={{ flex: 1, padding: '32px 24px', overflowY: 'auto' }}>
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
          {renderStep()}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '20px 24px', borderTop: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <button
          onClick={prevStep}
          disabled={step === 1}
          style={{ padding: '10px 20px', borderRadius: 12, border: `1px solid ${c.border}`, background: c.surface, color: c.text, fontSize: 14, fontWeight: 700, cursor: step === 1 ? 'not-allowed' : 'pointer', opacity: step === 1 ? 0.4 : 1 }}
        >
          Previous
        </button>

        {step === totalSteps ? (
          <button
            onClick={onSave}
            disabled={saving}
            style={{ flex: 1, padding: '12px 20px', borderRadius: 12, border: 'none', background: c.accent, color: '#fff', fontSize: 14, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: `0 4px 15px ${c.glow}40` }}
          >
            {saving ? 'Saving...' : 'Complete & Save'}
          </button>
        ) : (
          <button
            onClick={nextStep}
            style={{ flex: 1, padding: '12px 20px', borderRadius: 12, border: 'none', background: c.accent, color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', boxShadow: `0 4px 15px ${c.glow}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            Next Step <FiArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: isMobile ? 0 : 20
    }}>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
      />
      <motion.div
        initial={isMobile ? { y: '100%' } : { scale: 0.9, opacity: 0 }}
        animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
        exit={isMobile ? { y: '100%' } : { scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        style={{
          position: 'relative',
          width: isMobile ? '100%' : 600,
          height: isMobile ? '100%' : 'auto',
          maxHeight: isMobile ? '100%' : '90vh',
          zIndex: 2001
        }}
      >
        {modalContent}
      </motion.div>
    </div>
  );
};

export default Profile;
